/**
 * fragrance-intensity.js
 * Upload to: Assets folder in Shopify theme
 *
 * Infinite autoscroll slider driven by requestAnimationFrame.
 * Works for multiple instances of the section on the same page.
 * Pauses when the section leaves the viewport (IntersectionObserver).
 * Respects prefers-reduced-motion.
 */

(function () {
  'use strict';

  const SLIDE_GAP = 14; // px — must match CSS gap on .fis-slider-track

  /**
   * Initialise one slider viewport element.
   * @param {HTMLElement} viewport
   */
  function initSlider(viewport) {
    const track = viewport.querySelector('.fis-slider-track');
    if (!track) return;

    // Speed in px/second comes from data attribute (set by Liquid).
    const speed = parseFloat(viewport.dataset.speed) || 60;

    // Reduced-motion: skip animation entirely.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Collect only the original slides (not the pre-rendered clones).
    const originalSlides = Array.from(track.querySelectorAll('.fis-slide:not(.fis-slide--clone)'));
    if (!originalSlides.length) return;

    // Remove the server-rendered clones — JS will clone them fresh.
    track.querySelectorAll('.fis-slide--clone').forEach(el => el.remove());

    // Wait for images to load so we get correct offsetWidth values.
    function ready() {
      // Build one "set" width = sum of slides + gaps.
      const setWidth = originalSlides.reduce((acc, slide) => {
        return acc + slide.offsetWidth + SLIDE_GAP;
      }, 0);

      if (setWidth === 0) return; // bail if slides have no width yet

      // Clone the originals to create a seamless second set.
      originalSlides.forEach(slide => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.classList.add('fis-slide--clone');
        track.appendChild(clone);
      });

      // Set explicit track width so flex children don't wrap.
      track.style.width = setWidth * 2 + 'px';

      let offset = 0;
      let lastTs = null;
      let running = true;
      let raf = null;

      function tick(ts) {
        if (!running) return;

        if (lastTs !== null) {
          const delta = ts - lastTs;            // ms since last frame
          offset += (speed * delta) / 1000;    // px to advance

          // When we've scrolled one full set, reset — creates the loop.
          if (offset >= setWidth) {
            offset -= setWidth;
          }

          track.style.transform = 'translateX(-' + offset + 'px)';
        }

        lastTs = ts;
        raf = requestAnimationFrame(tick);
      }

      // Pause when section scrolls out of view (perf).
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              if (!running) return;
              lastTs = null;
              raf = requestAnimationFrame(tick);
            } else {
              cancelAnimationFrame(raf);
              lastTs = null;
            }
          });
        }, { threshold: 0 });

        io.observe(viewport.closest('.fis-section') || viewport);
      }

      // Kick off.
      raf = requestAnimationFrame(tick);
    }

    // If images might not be loaded yet, wait a tick.
    if (document.readyState === 'complete') {
      ready();
    } else {
      window.addEventListener('load', ready, { once: true });
    }
  }

  // Bootstrap all slider instances on the page.
  function boot() {
    document.querySelectorAll('.fis-slider-viewport').forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Support Shopify Theme Editor live reloads.
  document.addEventListener('shopify:section:load', function (e) {
    const viewport = e.target.querySelector('.fis-slider-viewport');
    if (viewport) initSlider(viewport);
  });

})();
